import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { WarningIcon } from "@phosphor-icons/react";

const REASON_PRESETS = {
  user: [
    "Harassment / hate speech",
    "Spam / scams",
    "Impersonation",
    "Underage / safety concern",
    "Other",
  ],
  listing: [
    "Counterfeit item",
    "Prohibited item",
    "Misleading description / photos",
    "Shill bidding",
    "Other",
  ],
  stream: [
    "Inappropriate content",
    "Hate speech / harassment",
    "Selling counterfeit / prohibited",
    "Underage host",
    "Other",
  ],
};

const TARGET_LABEL = {
  user: "user",
  listing: "listing",
  stream: "stream",
};

/**
 * Universal report button + dialog. Drop anywhere there's something
 * reportable. All reports flow into the admin reports inbox at /admin#reports
 * and notify every admin in real time.
 */
export default function ReportDialog({
  targetType,
  targetId,
  targetLabel,
  open,
  onOpenChange,
  trigger,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === "boolean";
  const visible = isControlled ? open : internalOpen;
  const setVisible = isControlled ? onOpenChange : setInternalOpen;

  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const text = reason.trim();
    if (text.length < 3) {
      toast.error("Please describe what's wrong (at least 3 characters).");
      return;
    }
    const path =
      targetType === "user"
        ? `/users/${targetId}/report`
        : targetType === "listing"
        ? `/listings/${targetId}/report`
        : `/streams/${targetId}/report`;
    setSubmitting(true);
    try {
      await api.post(path, {
        reason: text,
        category: category || null,
        context: targetLabel || null,
      });
      toast.success("Report submitted — thanks. Our team has been notified.");
      setReason("");
      setCategory("");
      setVisible(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const presets = REASON_PRESETS[targetType] || REASON_PRESETS.user;
  const noun = TARGET_LABEL[targetType] || "item";

  return (
    <>
      {!isControlled && trigger ? (
        <span onClick={() => setVisible(true)}>{trigger}</span>
      ) : null}
      <Dialog open={visible} onOpenChange={setVisible}>
        <DialogContent className="sm:max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50">
                <WarningIcon size={18} className="text-amber-600" />
              </span>
              <DialogTitle>Report this {noun}</DialogTitle>
            </div>
            <DialogDescription>
              Reports are sent privately to the Bidora admin team. We review
              every one and take action when our community guidelines are
              broken.
              {targetLabel ? (
                <span className="block mt-2 text-xs italic text-zinc-500">
                  Reporting: {targetLabel}
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                Reason
              </label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {presets.map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setCategory(p === category ? "" : p)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition ${
                      category === p
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                    }`}
                    data-testid={`report-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                Tell us more
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Briefly describe what happened. The more detail, the faster we can act."
                className="mt-1.5 text-sm"
                maxLength={400}
                data-testid="report-reason-textarea"
              />
              <div className="text-[11px] text-zinc-400 text-right mt-0.5 font-mono">
                {reason.length}/400
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setVisible(false)}
              disabled={submitting}
              data-testid="report-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || reason.trim().length < 3}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="report-submit-btn"
            >
              {submitting ? "Sending…" : "Send report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
